import React, { useEffect, useState } from 'react';

import { BaseControlProps } from '../../models/BaseControlProps';
import { Checkbox, CheckboxChange, DataTable, Pager, Span, } from '@tag/tag-components-react-v4';
import { UserAPIService } from '../../services/UserAPIService';
import { UserGetRequest } from '../../models/UserGetRequest';
import { UtilService } from '../../services/UtilService';
import { User } from '../../models/User';
import { useAuth } from '../../services/AuthContext';
import { UserDeleteRequest } from '../../models/UserDeleteRequest';
import SendMessage from '../../components/SendMessage/SendMessage';
import { 
    Box, 
    Button, 
    Card, 
    CardContent, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle, 
    Divider, 
    Grid, 
    IconButton, 
    Skeleton, 
    TextField, 
    Tooltip, 
    Typography, 
    useMediaQuery, 
    useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';
import { UserEnableDisableRequest } from '../../models/UserEnableDisableRequest';
import { UserResetPasswordRequest } from '../../models/UserResetPasswordRequest';
import ConfirmPrompt from '../../components/Common/ConfirmPrompt/ConfirmPrompt';
import { AdminUserCreateRequest } from '../../models/AdminUserCreateRequest';
import { UserUnlockAccountRequest } from '../../models/UserUnlockAccountRequest';


const AdminUsers = (props: BaseControlProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pageSize = 15;
    const [page, setPage] = useState(0);
    const [scopeGet, applyGet] = useState<UserGetRequest>({
        userId: 0,
        pageSize: pageSize,
        pageNo: page,
        filterSearchString: "",
        orderByString: ""
    });
    const [scopeCreate, applyCreate] = useState<AdminUserCreateRequest>({
        forename: "",
        surname: "",
        adminUserEmailAddress: "",
        ssoRequired: true
    });
    const [scopeDelete, applyDelete] = useState<UserDeleteRequest>({
        portaluserIds: ''
    });
    const [scopeEnableDisable, applyEnableDisable] = useState<UserEnableDisableRequest>({
        userId: 0,
        portalUserIds: '0'
    });
    const [scopeResetPassword, applyResetPassword] = useState<UserResetPasswordRequest>({
        portalUserId: ''
    });
    const [scopeUnlockAccount, applyUnlockAccount] = useState<UserUnlockAccountRequest>({
        portalUserId: ''
    });
    const [pageCount, setPageCount] = useState(0);
    const [createAdminModalVisible, setCreateAdminModalVisible] = useState(false);
    const [sendMessageModalVisible, setSendMessageModalVisible] = useState(false);
    const [loading, isLoading] = useState<boolean>(true);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState('none');
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [adminUsers, setAdminUsers] = useState<User[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [nameStr, setNameStr] = useState<string>('');
    const [emailStr, setEmailStr] = useState<string>('');
    const apiService = new UserAPIService();
    const authContext = useAuth();
    const { showMessage } = useMessage();
    const [forenameError, setForenameError] = React.useState(false);
    const [forenameErrorMessage, setForenameErrorMessage] = React.useState('');
    const [surnameError, setSurnameError] = React.useState(false);
    const [surnameErrorMessage, setSurnameErrorMessage] = React.useState('');
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    

    useEffect(() =>{
        const getAdminUsersAsync = async () => {
            await getAllAdminUsers();
        }
        getAdminUsersAsync();
    },[]);

    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeoutId = setTimeout(() => {
            const getAdminUsersAsync = async () => {
                await getAllAdminUsers();
            }

            getAdminUsersAsync();
        }, 900);

        setSearchTimeout(timeoutId);

        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [nameStr, emailStr])

    const getAllAdminUsers = () => {
        isLoading(true);
        scopeGet.filterSearchString = getSeachFilters();
        scopeGet.orderByString = getOrderByFilters();
        applyGet(scopeGet);
        apiService.getAllAdminUser(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                showMessage(response.error, 'error');
            }
            else {
                setAdminUsers(response.response?.users ?? []);
                setSelectedRows([]);
                setAllSelected(false);
                const pageCount =
                    response.response && response.response?.users.length > 0
                        ? Math.ceil(response.response.totalCount / pageSize)
                        : 0;
                setPageCount(pageCount && pageCount > 0 ? pageCount : 0);
            }
            isLoading(false);
        });        
    }

    const createAdminUser = () => {
        apiService.createAdminUser(scopeCreate, authContext.token).then((response) => {
            if (response.error) {
                showMessage(response.error, 'error');
            }
            else {
                if (response.response?.userCreated) {
                    showMessage('Admin user created successfully!', 'success');
                    resetCreateAdminForm();
                    setCreateAdminModalVisible(false);
                    getAllAdminUsers();
                } else {
                    showMessage(response.response?.reason ? response.response?.reason : "Unable to create user, please try again.", 'error');
                }
                
            }
        });
    }

    const getOrderByFilters = () => {
        return  `{
            "OrderByFields": [
              {
                "FieldName": "createdOn",
                "OrderDirection": "DESC"
              }
            ]
        }`
    }

    const getSeachFilters = () => {
        let searchFilters = [];
        if (nameStr.trim() !==  "") {
            searchFilters.push(`{"FieldName":"fullname","SearchType":"contains","SearchValue":"${nameStr}"}`);
        }

        if (emailStr.trim() !== "") {
            searchFilters.push(`{"FieldName":"emailAddress","SearchType":"equals","SearchValue":"${emailStr}"}`);
        }

        return searchFilters.length >= 0 ? `{"SearchFilters": [${searchFilters.join(",")}]}` : '';
    }

    const resetCreateAdminForm = () => {
        (document.getElementById('forename') as HTMLInputElement).value = '';
        (document.getElementById('surname') as HTMLInputElement).value = '';
        (document.getElementById('email') as HTMLInputElement).value = '';
    }

    const deleteAdminUser = () => {
        scopeDelete.portaluserIds = selectedRows.join(', ');
        applyDelete(scopeDelete);
        apiService.deleteUser(scopeDelete, authContext.token).then((response) => {
            if (response.error) {
                showMessage(response.error, 'error');
            }
            else {
                showMessage('User Deleted Successfully', 'success');
                getAllAdminUsers();
            }

            setConfirmLoading(false);
            setConfirmVisible(false);
        });
    }

    const enableDisableAdminUser = () => {
        scopeEnableDisable.portalUserIds = selectedRows.join(', ');
        scopeEnableDisable.disable = true;
        scopeEnableDisable.enable = false;
        applyEnableDisable(scopeEnableDisable);
        apiService.enableDisableUser(scopeEnableDisable, authContext.token).then((response) => {
            if (response.error) {
                showMessage(response.error, 'error');
            }
            else {
                showMessage('User Enabled/Disabled Successfully', 'success');
                getAllAdminUsers();
            }
            
            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    }
    
    const resetPassword = () => {
        scopeResetPassword.portalUserId = selectedRows.join(', ');
        applyResetPassword(scopeResetPassword);
        apiService.resetUserPassword(scopeResetPassword, authContext.token).then((response) => {
            if (response.error) {
                showMessage(response.error, 'error');
            }
            else {
                showMessage('User Password Reseted Successfully', 'success');
                getAllAdminUsers();
            }

            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    }

    const unlockUserAccount = () => {
        scopeUnlockAccount.portalUserId = selectedRows.join(', ');
        applyUnlockAccount(scopeUnlockAccount);
        apiService.unlockUserAccount(scopeUnlockAccount, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }

                showMessage(response.error, 'error');
            }
            else {
                showMessage('User Account Unlocked Successfully', 'success');
                getAllAdminUsers();
            }

            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    }

    // Handle header checkbox click (Select All)
    const handleSelectAll = (event: CheckboxChange) => {
        if (event.checked) {
            const allRowIds = adminUsers.map((row) => row.userId);
            setSelectedRows(allRowIds); // Select all rows
            setAllSelected(true);
        } else {
            setAllSelected(false);
            setSelectedRows([]); // Deselect all rows
        }
    };

    // Handle individual row checkbox click
    const handleRowSelect = (userId: number) => {
        setSelectedRows((prevSelected) =>
            prevSelected.includes(userId)
                ? prevSelected.filter((id) => id !== userId) // Deselect
                : [...prevSelected, userId] // Select
        );
    };

    const resetFilters = () => {
        setNameStr('');
        setEmailStr('');
    }


    function renderDisableButton(userId: number) {
        return (
            <Tooltip title="Disable/Enable User">
                <IconButton
                    aria-label='disable-enable'
                    size='small'
                    color='primary'
                    onClick={() => {
                        setConfirmTitle('Disable/Enable User');
                        setConfirmMessage("Are you sure you want to enable/disable this Admin User?");
                        handleRowSelect(userId);
                        showConfirmation('disable')
                    }}
                >
                    <BlockIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderDeleteButton(userId: number) {
        return (
            <Tooltip title="Delete User">
                <IconButton
                    aria-label='delete-admins'
                    size='small'
                    color='error'
                    onClick={() => {
                        setConfirmTitle('Delete Admin User');
                        setConfirmMessage("Are you sure you want to delete this Admin User?");
                        handleRowSelect(userId);
                        showConfirmation('delete')
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderResetPasswordButton(userId: number) {
        return (
            <Tooltip title="Reset User Password">
                <IconButton
                    aria-label='reset-password'
                    size='small'
                    color='default'
                    onClick={() => {
                        setConfirmTitle('Reset User Password');
                        setConfirmMessage("Are you sure you want to reset this Admin User's password ?");
                        handleRowSelect(userId);
                        showConfirmation('reset');
                    }}
                >
                    <LockResetIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderUnlockAccountButton(userId: number) {
        return (
            <Tooltip title="Unlock User Account">
                <IconButton
                    aria-label='unlock-account'
                    size='small'
                    color='success'
                    onClick={() => {
                        setConfirmTitle('Unlock User Account');
                        setConfirmMessage("Are you sure you want to unlock this User account ?");
                        handleRowSelect(userId);
                        showConfirmation('unlock');
                    }}
                >
                    <LockOpenIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderHeaderSelect() {
        setAllSelected(allSelected);
        return (
            <Checkbox 
                checked={allSelected}
                onChange={handleSelectAll}
            />
        );
    }

    function renderRowSelect({ row }: any) {
        const isSelected = selectedRows.includes(row.original.userId) || allSelected;
        return (
            <Checkbox 
                checked={isSelected}
                onChange={() => handleRowSelect(row.original.userId)}
            />
        );
    }

    function renderActionButtons(user: User) {
        return (
            authContext.isSuperAdmin === 'true' && (
                <div style={{ display: 'flex', justifyContent: 'right', gap: '8px' }}>
                    {user.locked ? renderUnlockAccountButton(user.userId) : ''}
                    {renderDisableButton(user.userId)}
                    {renderDeleteButton(user.userId)}
                    {renderResetPasswordButton(user.userId)}
                </div>
            )
        );
    }

    const columns = React.useMemo(
        () => [
            { accessorKey: 'userId', header: renderHeaderSelect, cell: renderRowSelect },
            {  
                header: 'Fullname',
                cell: ({ row }: any) => {
                    return getUserFullName(row.original);
                }
            },
            { 
                accessorKey: 'emailAddress', 
                header: 'Email / Username',
                cell: ({ row }: any) => {
                    return row.original.emailAddress.trim() === '' ? row.original.userName : row.original.emailAddress;
                } 
            },
            { accessorKey: 'enabled', header: 'Enabled',  meta: { dataType: 'boolean' } },
            { accessorKey: 'locked', header: 'Locked',  meta: { dataType: 'boolean' } },
            {
                accessorKey: 'actions',
                header: () => <div style={{ textAlign: 'center' }}>Actions</div>,
                cell: ({ row }: any) => {
                    return renderActionButtons(row.original);
                },
                meta: {
                    getCellProps: () => {
                        return { style: { textAlign: 'center', width: '120px' } };
                    },
                },
            }
        ],
        [allSelected, selectedRows],
    );

    const getUserFullName = (user: User) => {
        const fullname = user.forename + ' ' + user.surname;
        return fullname.trim() === '' ? user.userName : fullname; 
    }

    const handleClose = () => {
        setCreateAdminModalVisible(false)
    }

    const handlePageChange = async (e: any) => {
        setPage(e.page - 1);
        scopeGet.pageNo = e.page - 1;
        applyGet(scopeGet);

        await getAllAdminUsers();
    };

    const handleConfirmCancel = () => {
        setConfirmVisible(false);
    }

    const handleConfirmConfirmed = async () => {
        setConfirmLoading(true);

        if (confirmAction === 'disable') {
            await enableDisableAdminUser();
        }
        else if (confirmAction === 'delete') {
            await deleteAdminUser();
        }
        else if (confirmAction === 'reset') {
            await resetPassword();
        }
        else if (confirmAction === 'unlock') {
            await unlockUserAccount();
        }
    }

    const showConfirmation = (action: string) => {
        setConfirmAction(action);
        setConfirmVisible(true);
    }

    function applyScopeCreate(){
        applyCreate(UtilService.cloneObject(scopeCreate));
    }

    const validateInputs = () => {
        const forename = document.getElementById('forename') as HTMLInputElement;
        const surname = document.getElementById('surname') as HTMLInputElement;
        const email = document.getElementById('email') as HTMLInputElement;
        // const password = document.getElementById('password') as HTMLInputElement;
        // const confirmPassword = document.getElementById('confirmPassword') as HTMLInputElement;
        // const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_])[A-Za-z\d@$!%*?&_]{8,}$/;
        scopeCreate.forename = forename.value;
        scopeCreate.surname = surname.value;
        scopeCreate.adminUserEmailAddress = email.value;
        applyScopeCreate();
        let isValid = true;
        
        if (!forename.value) {
            setForenameError(true);
            setForenameErrorMessage('First name is required');
            isValid = false;
        } else {
            setForenameError(false);
            setForenameErrorMessage('');
        }

        if (!surname.value) {
            setSurnameError(true);
            setSurnameErrorMessage('Last name is required');
            isValid = false;
        } else {
            setSurnameError(false);
            setSurnameErrorMessage('');
        }

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
          setEmailError(true);
          setEmailErrorMessage('Please enter a valid email address.');
          isValid = false;
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
    
        return isValid;
    };

    const renderheaderView = () => {
        return (
            <Box sx={{ width: '100%', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        Manage Admin Users
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {authContext.isSuperAdmin === 'true' && (
                            <Button 
                                variant='outlined'
                                onClick={() => { setCreateAdminModalVisible(true) }} 
                                startIcon={<AddIcon/>}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                Create Admin Users
                            </Button>
                        )}
                    </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center" >
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Name"
                            variant='outlined'
                            value={nameStr}
                            onChange={(e) => setNameStr(e.target.value )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            variant='outlined'
                            value={emailStr}
                            onChange={(e) => setEmailStr(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            size='large'
                            fullWidth
                            onClick={() => {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                getAllAdminUsers();
                            }}
                        >
                            Filter
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            style={{ minWidth: '80px' }}
                            onClick={() => {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                resetFilters();
                            }}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Grid container spacing={2} justifyContent="flex-start">
                        <Grid item xs={12} sm="auto">
                            <Button 
                                disabled={selectedRows.length === 0}
                                aria-label='enable-disable'
                                color='primary'
                                startIcon={<BlockIcon/>}
                                onClick={() => {
                                    setConfirmTitle('Disable/Enable Admin User');
                                    setConfirmMessage("Are you sure you want to enable/disable all these Admin User?");
                                    showConfirmation('disable')
                                }}
                            >
                                Disable / Enable
                            </Button>
                        </Grid>
                        {authContext.isSuperAdmin === 'true' && (
                            <Grid item xs={12} sm="auto">
                                <Button 
                                    disabled={selectedRows.length === 0}
                                    aria-label='delete'
                                    color='error'   
                                    startIcon={<DeleteIcon/>}
                                    onClick={() => {
                                        setConfirmTitle('Delete Admin User');
                                        setConfirmMessage("Are you sure you want to delete all these Admin User's ?");
                                        showConfirmation('delete')
                                    }}
                                >
                                    Delete
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>
        );
    }

    const renderCreateAdminModal = () => {
        return (
            <Dialog
                open={createAdminModalVisible}
                onClose={handleClose}
                PaperProps={{
                component: 'form',
                onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    createAdminUser();
                },
                }}
            >
                <DialogTitle>Create Admin User</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please fill in the details
                    </DialogContentText>
                        <TextField 
                            required
                            fullWidth
                            id="forename"
                            label="First Name"
                            name="forename"
                            variant="standard"
                            margin='dense'
                            type='text'
                            error={forenameError}
                            helperText={forenameErrorMessage}
                            color={forenameError ? 'error' : 'primary' }
                        />
                        <TextField 
                            required
                            fullWidth
                            id="surname"
                            label="Last Name"
                            name="surname"
                            variant="standard"
                            margin='dense'
                            type='text'
                            error={surnameError}
                            helperText={surnameErrorMessage}
                            color={surnameError ? 'error' : 'primary' }
                        />
                        <TextField 
                            required
                            fullWidth
                            id="email"
                            label="you@email.com"
                            name="email"
                            autoComplete="email"
                            variant="standard"
                            margin='dense'
                            type='email'
                            error={emailError}
                            helperText={emailErrorMessage}
                            color={emailError ? 'error' : 'primary' }
                        />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="submit" variant='contained' onClick={validateInputs}>Save</Button>
                </DialogActions>
            </Dialog>
        );
    }

    const renderConfirmPrompt = () => {
        return (
            <ConfirmPrompt 
                open={confirmVisible}
                title={confirmTitle}
                message={confirmMessage}
                onConfirm={handleConfirmConfirmed}
                onCancel={handleConfirmCancel}
                isLoading={confirmLoading}
            />
        );
    }

    const EmptyState = () => (
        <Box 
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No payslips found
          </Typography>
        </Box>
    );

    // Loading skeleton for mobile
    const MobileLoadingSkeleton = () => (
        <Box sx={{ mb: 2 }}>
        {[...Array(3)].map((_, index) => (
            <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Box display="flex" justifyContent="flex-end">
                <Skeleton variant="rectangular" width={100} height={36} />
                </Box>
            </CardContent>
            </Card>
        ))}
        </Box>
    );

    if (isMobile) {
            return (
                <>
                    {renderheaderView()}  
    
                    <div>
                        {loading ? (
                        <MobileLoadingSkeleton />
                            ) : adminUsers.length === 0 ? (
                            <EmptyState />
                            ) : (
                            adminUsers.map((user) => (
                                <Card key={user.userId} sx={{ mb: 1 }}>
                                    <CardContent>
                                        <Typography variant="subtitle2">
                                        Fullname: {getUserFullName(user)}
                                        </Typography>
                                        <Typography variant="subtitle2" color="text.secondary">
                                        Username: {user.emailAddress.trim() === '' ? user.userName : user.emailAddress}
                                        </Typography>
                                        <Typography variant="subtitle2" color="text.secondary">
                                        Enabled: {user.enabled.toString().toUpperCase()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                        Locked: {user.locked.toString().toUpperCase()}
                                        </Typography>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        {renderActionButtons(user)}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                       {!loading && adminUsers.length > 0 && (
                            <Pager
                                accent='blue'
                                page={page + 1}
                                pageCount={pageCount}
                                onPageRequest={(e) => { handlePageChange(e); }}
                                style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                            />
                        )}
                    </div>
                    
                    {authContext.isSuperAdmin === 'true' && (
                        renderCreateAdminModal()
                    )}

                    <SendMessage visible={sendMessageModalVisible} setVisible={setSendMessageModalVisible} />
                    
                    {renderConfirmPrompt()}
                </>
            );
        }

    return(
        <>
            {renderheaderView()}

            <div style={{ background: '#fff' }}>
                <div style={{ height: 'auto' }}>
                    <DataTable 
                        isLoading={loading} 
                        loadingElement={<Span accent="blue">Loading ...</Span>} 
                        accent='blue' 
                        columns={columns} 
                        data={adminUsers} />
                </div>
                <Pager
                    accent='blue'
                    page={page + 1}
                    pageCount={pageCount}
                    onPageRequest={(e) => { handlePageChange(e); }}
                    style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                />
            </div>
            
            {authContext.isSuperAdmin === 'true' && (
                renderCreateAdminModal()
            )}

            <SendMessage visible={sendMessageModalVisible} setVisible={setSendMessageModalVisible} />
            
            {renderConfirmPrompt()}
        </>
    )
}
export default AdminUsers;